import { toast } from 'sonner';

import { MODAL_ROUTE } from '@/constants';
import {
    useDeleteCustomerByIdMutation,
    useGetCustomersQuery,
    useUpdateCustomerByIdMutation,
} from '@/features/slice/customers/customer-api-slice';
import { useModal, useUrlFilter, useUrlPagination } from '@/hooks';
import { toastError } from '@/utils';

export const useCustomerList = () => {
    const { openModal } = useModal();
    const { page, limit } = useUrlPagination();
    const { debouncedSearchText, sortBy, sortOrder, getSearchFilter } =
        useUrlFilter();

    const queryParams = {
        customer_type: getSearchFilter('customer_type') || undefined,
        page,
        limit,
        search: debouncedSearchText,
        sort_by: sortBy,
        sort_order: sortOrder,
    };

    const { data, isLoading, isError, isFetching, error } =
        useGetCustomersQuery(queryParams);

    const [deleteCustomerById, { isLoading: isDeleting }] =
        useDeleteCustomerByIdMutation();

    const handleAddCustomer = () => {
        openModal(MODAL_ROUTE.CREATE_CUSTOMER);
    };

    const handleEditCustomer = (customerId: string) => {
        openModal(MODAL_ROUTE.UPDATE_CUSTOMER, customerId);
    };

    const [updateCustomerById, { isLoading: isUpdating }] =
        useUpdateCustomerByIdMutation();

    const handleSuspendCustomer = async (customer: any) => {
        const confirmed = window.confirm(
            'Are you sure you want to suspend this customer?',
        );
        if (!confirmed) return;

        const payload = {
            status: 'suspended',
            id: customer.id,
            name: customer.name,
            email: customer.email,
            customer_type: customer.customer_type,
            phone: customer.phone,
            address: customer.address,
            password: customer.password,
        };
        try {
            await updateCustomerById(payload).unwrap();
            toast.success('Customer has been suspended');
        } catch (error) {
            toastError(error, 'useCustomerList: handleSuspendCustomer');
        }
    };

    const handleActivateCustomer = async (customer: any) => {
        const confirmed = window.confirm(
            'Are you sure you want to activate this customer?',
        );
        if (!confirmed) return;

        const payload = {
            status: 'active',
            id: customer.id,
            name: customer.name,
            email: customer.email,
            customer_type: customer.customer_type,
            phone: customer.phone,
            address: customer.address,
            password: customer.password,
        };

        try {
            await updateCustomerById(payload).unwrap();

            toast.success('Customer has been activated');
        } catch (error) {
            toastError(error, 'useCustomerList: handleActivateCustomer');
        }
    };

    const handleDeleteCustomer = async (_customerId: string) => {
        const confirmed = window.confirm(
            'Are you sure you want to delete this customer? This action cannot be undone.',
        );
        if (!confirmed) return;

        try {
            await deleteCustomerById(_customerId).unwrap();
            toast.success('Customer has been deleted');
        } catch (error) {
            toastError(error, 'useCustomerList: handleDeleteCustomer');
        }
    };

    return {
        data,
        isLoading,
        isError,
        isFetching,
        error,
        handleAddCustomer,
        handleEditCustomer,
        handleDeleteCustomer,
        isDeleting,

        handleSuspendCustomer,
        handleActivateCustomer,
        isUpdating,
    };
};
